/**
 * Repository and source revision metadata used to make generated wiki citations stable.
 * `sourceCommitSha` and `sourceBranch` accept both `null` and `undefined` because Prisma
 * rows use `null`, while API/UI callers often omit missing fields.
 */
export type WikiSourceLinkOptions = {
  owner: string;
  repo: string;
  sourceCommitSha?: string | null;
  sourceBranch?: string | null;
};

/**
 * A source reference parsed from generated wiki markdown, such as
 * `AGENTS.md:1-25`, `README.md`, or `README.md#section`.
 */
export type WikiSourceReference = {
  filePath: string;
  lineStart?: string;
  lineEnd?: string;
  query?: string;
  anchor?: string;
};

export type WikiSourceLinkPatternCounts = {
  sameRepoDefaultBranchBlobLinks: number;
  emptyHrefSourceLinks: number;
  relativeMarkdownLinks: number;
};

export type WikiSourceLinkNormalizationDiagnostics = {
  sourceRef: string;
  changed: boolean;
  before: WikiSourceLinkPatternCounts;
  after: WikiSourceLinkPatternCounts;
};

type CacheNormalizationResult = {
  value: unknown;
  changed: boolean;
  before: WikiSourceLinkPatternCounts;
  after: WikiSourceLinkPatternCounts;
};

const CONTENT_KEYS = new Set(['content', 'contentMd', 'markdownContent']);
const ABSOLUTE_DEFAULT_BRANCH_BLOB_REGEX =
  /https:\/\/github\.com\/([^/\s)]+)\/([^/\s)]+)\/blob\/(main|master)\/([^\s)]+)/gi;
const EMPTY_HREF_SOURCE_REGEX = /\[([^\]]+)\]\(\)/g;
const RELATIVE_MARKDOWN_LINK_REGEX = /\]\((?![a-z][a-z0-9+.-]*:|[#/?])([^)]+)\)/gi;
const EMPTY_PATTERN_COUNTS: WikiSourceLinkPatternCounts = {
  sameRepoDefaultBranchBlobLinks: 0,
  emptyHrefSourceLinks: 0,
  relativeMarkdownLinks: 0,
};

function safeDecodeUriComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function encodePathSegments(value: string): string {
  return value
    .split('/')
    .map((segment) => encodeURIComponent(safeDecodeUriComponent(segment)))
    .join('/');
}

function normalizeRepositorySegment(value: string): string {
  return safeDecodeUriComponent(value).toLowerCase();
}

export function isSameWikiSourceRepository(
  owner: string,
  repo: string,
  linkOwner: string,
  linkRepo: string,
): boolean {
  return (
    normalizeRepositorySegment(owner) === normalizeRepositorySegment(linkOwner) &&
    normalizeRepositorySegment(repo) === normalizeRepositorySegment(linkRepo)
  );
}

function countMatches(content: string, regex: RegExp): number {
  return Array.from(content.matchAll(regex)).length;
}

function addPatternCounts(
  left: WikiSourceLinkPatternCounts,
  right: WikiSourceLinkPatternCounts,
): WikiSourceLinkPatternCounts {
  return {
    sameRepoDefaultBranchBlobLinks:
      left.sameRepoDefaultBranchBlobLinks + right.sameRepoDefaultBranchBlobLinks,
    emptyHrefSourceLinks: left.emptyHrefSourceLinks + right.emptyHrefSourceLinks,
    relativeMarkdownLinks: left.relativeMarkdownLinks + right.relativeMarkdownLinks,
  };
}

function splitOnce(value: string, separator: string): [string, string | undefined] {
  const separatorIndex = value.indexOf(separator);
  if (separatorIndex === -1) {
    return [value, undefined];
  }

  return [value.slice(0, separatorIndex), value.slice(separatorIndex + separator.length)];
}

function countWikiSourceLinkPatterns(
  content: string,
  options: WikiSourceLinkOptions,
): WikiSourceLinkPatternCounts {
  const sameRepoDefaultBranchBlobLinks = Array.from(
    content.matchAll(ABSOLUTE_DEFAULT_BRANCH_BLOB_REGEX),
  ).filter((match) =>
    isSameWikiSourceRepository(options.owner, options.repo, match[1] ?? '', match[2] ?? ''),
  );

  return {
    sameRepoDefaultBranchBlobLinks: sameRepoDefaultBranchBlobLinks.length,
    emptyHrefSourceLinks: countMatches(content, EMPTY_HREF_SOURCE_REGEX),
    relativeMarkdownLinks: countMatches(content, RELATIVE_MARKDOWN_LINK_REGEX),
  };
}

/**
 * Returns the best GitHub ref for wiki source links.
 *
 * Completed wiki versions should point at their pinned source commit when available.
 * Older rows may only have the default branch, and legacy rows may have neither.
 */
export function resolveWikiSourceRef({
  sourceCommitSha,
  sourceBranch,
}: Pick<WikiSourceLinkOptions, 'sourceCommitSha' | 'sourceBranch'>): string {
  const commitSha = sourceCommitSha?.trim();
  if (commitSha) {
    return commitSha;
  }

  const branch = sourceBranch?.trim();
  if (branch) {
    return branch;
  }

  return 'HEAD';
}

export function parseWikiSourceReference(value: string): WikiSourceReference {
  const trimmed = value.trim();
  const [valueBeforeHash, hashAnchor] = splitOnce(trimmed, '#');
  const [filePathWithRange, query] = splitOnce(valueBeforeHash, '?');
  // Note: Source lists sometimes compress multiple ranges into one link label.
  // GitHub cannot link to disjoint ranges in a single URL, so we anchor to the first range.
  const lineRangeMatch = (filePathWithRange ?? trimmed).match(
    /^(.*?):(\d+)(?:-(\d+))?(?:\s*,.*)?$/,
  );

  if (!lineRangeMatch) {
    return {
      filePath: filePathWithRange ?? trimmed,
      query: query === undefined ? undefined : `?${query}`,
      anchor: hashAnchor ? `#${hashAnchor}` : undefined,
    };
  }

  return {
    filePath: lineRangeMatch[1]?.trim() ?? trimmed,
    lineStart: lineRangeMatch[2],
    lineEnd: lineRangeMatch[3],
    query: query === undefined ? undefined : `?${query}`,
    anchor: hashAnchor ? `#${hashAnchor}` : undefined,
  };
}

export function buildWikiSourceLineAnchor(reference: WikiSourceReference): string {
  if (!reference.lineStart) {
    return '';
  }

  if (reference.lineEnd && reference.lineEnd !== reference.lineStart) {
    return `#L${reference.lineStart}-L${reference.lineEnd}`;
  }

  return `#L${reference.lineStart}`;
}

function buildGitHubBlobUrl(
  options: WikiSourceLinkOptions,
  sourceRef: string,
  reference: WikiSourceReference,
): string {
  const encodedOwner = encodeURIComponent(safeDecodeUriComponent(options.owner));
  const encodedRepo = encodeURIComponent(safeDecodeUriComponent(options.repo));
  const encodedSourceRef = encodePathSegments(sourceRef);
  const encodedFilePath = encodePathSegments(reference.filePath.replace(/^\.?\//, ''));

  return `https://github.com/${encodedOwner}/${encodedRepo}/blob/${encodedSourceRef}/${encodedFilePath}${reference.query || ''}${buildWikiSourceLineAnchor(reference) || reference.anchor || ''}`;
}

/**
 * Rewrites wiki markdown links that reference repository source files.
 *
 * This repairs already-generated wiki content that hardcoded `main` or left citation hrefs
 * empty, and it keeps future renderer fallbacks aligned with server-side normalization.
 */
export function normalizeWikiSourceLinks(content: string, options: WikiSourceLinkOptions): string {
  if (!content || !options.owner || !options.repo) {
    return content;
  }

  const sourceRef = resolveWikiSourceRef(options);

  return content
    .replace(
      ABSOLUTE_DEFAULT_BRANCH_BLOB_REGEX,
      (match, linkOwner: string, linkRepo: string, _branch: string, encodedFilePath: string) => {
        if (!isSameWikiSourceRepository(options.owner, options.repo, linkOwner, linkRepo)) {
          return match;
        }

        return buildGitHubBlobUrl(options, sourceRef, {
          ...parseWikiSourceReference(encodedFilePath),
        });
      },
    )
    .replace(EMPTY_HREF_SOURCE_REGEX, (_match, label: string) => {
      const reference = parseWikiSourceReference(label);
      const url = buildGitHubBlobUrl(options, sourceRef, reference);
      return `[${label}](${url})`;
    })
    .replace(RELATIVE_MARKDOWN_LINK_REGEX, (_match, target: string) => {
      const reference = parseWikiSourceReference(target);
      const url = buildGitHubBlobUrl(options, sourceRef, reference);
      return `](${url})`;
    });
}

export function normalizeWikiSourceLinksWithDiagnostics(
  content: string,
  options: WikiSourceLinkOptions,
): { content: string; diagnostics: WikiSourceLinkNormalizationDiagnostics } {
  if (!content || !options.owner || !options.repo) {
    const sourceRef = resolveWikiSourceRef(options);
    return {
      content,
      diagnostics: {
        sourceRef,
        changed: false,
        before: EMPTY_PATTERN_COUNTS,
        after: EMPTY_PATTERN_COUNTS,
      },
    };
  }

  const normalized = normalizeWikiSourceLinks(content, options);
  const changed = normalized !== content;
  const sourceRef = resolveWikiSourceRef(options);
  // Count link patterns only when diagnostics can produce an actionable warning.
  const shouldCollectPatternCounts = changed || sourceRef === 'HEAD';
  const before = shouldCollectPatternCounts
    ? countWikiSourceLinkPatterns(content, options)
    : EMPTY_PATTERN_COUNTS;
  const after = shouldCollectPatternCounts
    ? countWikiSourceLinkPatterns(normalized, options)
    : EMPTY_PATTERN_COUNTS;

  return {
    content: normalized,
    diagnostics: {
      sourceRef,
      changed,
      before,
      after,
    },
  };
}

function normalizeCacheValue(
  value: unknown,
  key: string | null,
  options: WikiSourceLinkOptions,
): CacheNormalizationResult {
  if (typeof value === 'string') {
    if (!key || !CONTENT_KEYS.has(key)) {
      return {
        value,
        changed: false,
        before: EMPTY_PATTERN_COUNTS,
        after: EMPTY_PATTERN_COUNTS,
      };
    }

    const normalized = normalizeWikiSourceLinksWithDiagnostics(value, options);
    return {
      value: normalized.content,
      changed: normalized.diagnostics.changed,
      before: normalized.diagnostics.before,
      after: normalized.diagnostics.after,
    };
  }

  if (Array.isArray(value)) {
    let changed = false;
    let before = EMPTY_PATTERN_COUNTS;
    let after = EMPTY_PATTERN_COUNTS;
    let normalizedItems: unknown[] | null = null;

    value.forEach((item, index) => {
      const normalizedItem = normalizeCacheValue(item, null, options);
      changed ||= normalizedItem.changed;
      before = addPatternCounts(before, normalizedItem.before);
      after = addPatternCounts(after, normalizedItem.after);

      if (normalizedItem.changed && !normalizedItems) {
        normalizedItems = value.slice(0, index);
      }

      normalizedItems?.push(normalizedItem.value);
    });

    return { value: changed ? (normalizedItems ?? value) : value, changed, before, after };
  }

  if (value && typeof value === 'object') {
    let changed = false;
    let before = EMPTY_PATTERN_COUNTS;
    let after = EMPTY_PATTERN_COUNTS;
    const sourceRecord = value as Record<string, unknown>;
    let normalizedRecord: Record<string, unknown> | null = null;

    Object.entries(sourceRecord).forEach(([entryKey, entryValue]) => {
      const normalizedEntry = normalizeCacheValue(entryValue, entryKey, options);
      changed ||= normalizedEntry.changed;
      before = addPatternCounts(before, normalizedEntry.before);
      after = addPatternCounts(after, normalizedEntry.after);

      if (normalizedEntry.changed) {
        normalizedRecord ??= { ...sourceRecord };
        normalizedRecord[entryKey] = normalizedEntry.value;
      }
    });

    return { value: changed ? (normalizedRecord ?? value) : value, changed, before, after };
  }

  return {
    value,
    changed: false,
    before: EMPTY_PATTERN_COUNTS,
    after: EMPTY_PATTERN_COUNTS,
  };
}

/**
 * Normalizes only markdown-bearing fields in a wiki cache payload.
 *
 * The traversal returns the original object when no nested value changes so callers can use it
 * safely on cached data without forcing unnecessary object churn.
 */
export function normalizeWikiCacheDataSourceLinks<T>(
  cacheData: T,
  options: WikiSourceLinkOptions,
): T {
  return normalizeCacheValue(cacheData, null, options).value as T;
}

export function normalizeWikiCacheDataSourceLinksWithDiagnostics<T>(
  cacheData: T,
  options: WikiSourceLinkOptions,
): { cacheData: T; diagnostics: WikiSourceLinkNormalizationDiagnostics } {
  const normalized = normalizeCacheValue(cacheData, null, options);

  return {
    cacheData: normalized.value as T,
    diagnostics: {
      sourceRef: resolveWikiSourceRef(options),
      changed: normalized.changed,
      before: normalized.before,
      after: normalized.after,
    },
  };
}
